import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';

export type WedgeBv2ShellLook =
  | 'bv2shell-back-bar' | 'bv2shell-back-inline'
  | 'bv2shell-dock-cell' | 'bv2shell-dock-cell-primary' | 'bv2shell-dock-cell-link' | 'bv2shell-dock-tray'
  | 'bv2shell-dock-action-primary' | 'bv2shell-dock-action-cyan' | 'bv2shell-dock-action-outline' | 'bv2shell-feedback-chip'
  | 'bv2shell-person' | 'bv2shell-full-brief' | 'bv2shell-up-next'
  | 'bv2shell-answer' | 'bv2shell-next'
  | 'bv2shell-hero-action' | 'bv2shell-refresh';

const LOOKS = new Set<string>([
  'bv2shell-back-bar', 'bv2shell-back-inline', 'bv2shell-dock-cell', 'bv2shell-dock-cell-primary', 'bv2shell-dock-cell-link', 'bv2shell-dock-tray',
  'bv2shell-dock-action-primary', 'bv2shell-dock-action-cyan', 'bv2shell-dock-action-outline',
  'bv2shell-feedback-chip', 'bv2shell-person', 'bv2shell-full-brief', 'bv2shell-up-next', 'bv2shell-answer', 'bv2shell-next', 'bv2shell-hero-action', 'bv2shell-refresh',
]);
export const isWedgeBv2ShellLook = (look: string | undefined): look is WedgeBv2ShellLook => look !== undefined && LOOKS.has(look);
export const wedgeBv2ShellDrawsHit = (look: string): boolean => !isWedgeBv2ShellLook(look);

/* Released brief v2 shell, dock, pocket and rep check controls (prep-brief-v2), off-scale. Each look holds the shipped geometry and type; the caller
   sets the font family and points the --wedge-bv2-* roles at the brief scope vars. Roles hold one value each: bc (border color), bg, ink,
   shadow, line, cursor. A bare button keeps the browser padding, as shipped. Outside the brief each role falls back
   to a base token. No look draws a transparent target: the shipped boxes had none (wedgeBv2ShellDrawsHit). */
const role = (name: string, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;
const row = {display: 'inline-flex', alignItems: 'center'} as const;

export function wedgeBv2ShellStyle(look: WedgeBv2ShellLook): CSSProperties {
  switch (look) {
    case 'bv2shell-back-bar': return {
      height: 40, padding: '0 12px 0 8px', borderRadius: 12, border: 'none', background: 'transparent', color: role('ink', T.accent),
      fontSize: 15, fontWeight: 600, cursor: 'pointer', ...row, gap: 6,
    };
    case 'bv2shell-back-inline': return {
      height: 36, padding: '0 12px 0 6px', borderRadius: 10, border: 'none', background: 'transparent', color: role('ink', T.accent),
      fontSize: 14, fontWeight: 600, cursor: 'pointer', ...row, gap: 4,
    };
    case 'bv2shell-dock-cell': return {
      minWidth: 0, height: 52, borderRadius: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
      border: `1px solid ${role('bc', T.separator)}`, background: role('bg', T.surfaceAlt), color: role('ink', T.label),
    };
    case 'bv2shell-dock-cell-primary': return {
      ...wedgeBv2ShellStyle('bv2shell-dock-cell'), border: 'none', background: role('bg', T.accent), color: role('ink', T.label), boxShadow: role('shadow', 'none'),
    };
    case 'bv2shell-dock-cell-link': return {
      ...wedgeBv2ShellStyle('bv2shell-dock-cell'), boxSizing: 'border-box', textDecoration: 'none',
    };
    case 'bv2shell-dock-tray': return {
      height: 46, padding: '0 12px', borderRadius: 12, cursor: 'pointer', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
      border: `1px solid ${role('bc', T.separator)}`, background: role('bg', T.surfaceAlt), color: role('ink', T.label), fontSize: 14, fontWeight: 600,
    };
    case 'bv2shell-dock-action-primary': return {
      height: 46, padding: '0 18px', borderRadius: 14, ...row, gap: 8, border: 'none', background: role('bg', T.accent), color: role('ink', T.label),
      fontSize: 15, fontWeight: 600, cursor: 'pointer',
    };
    case 'bv2shell-dock-action-cyan': return {
      ...wedgeBv2ShellStyle('bv2shell-dock-action-primary'), border: `1px solid ${role('bc', T.separator)}`, background: role('bg', T.surfaceAlt), color: role('ink', T.label),
    };
    case 'bv2shell-dock-action-outline': return {
      height: 46, padding: '0 18px', borderRadius: 14, border: `1px solid ${role('bc', T.separator)}`, background: 'transparent', color: role('ink', T.label),
      fontSize: 15, fontWeight: 600, cursor: 'pointer',
    };
    case 'bv2shell-feedback-chip': return {
      height: 36, padding: '0 12px', borderRadius: 10, border: `1px solid ${role('line', T.separator)}`, background: role('bg', 'transparent'),
      color: role('ink', T.labelSec), fontSize: 12, fontWeight: 500, cursor: role('cursor', 'pointer') as CSSProperties['cursor'],
    };
    case 'bv2shell-person': return {
      width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', cursor: role('cursor', 'pointer') as CSSProperties['cursor'], padding: 0,
      border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', gap: 12,
    };
    case 'bv2shell-full-brief': return {
      height: 50, borderRadius: 16, border: `1px solid ${role('ink', T.accent)}`, background: 'transparent', cursor: 'pointer',
      color: role('ink', T.accent), fontSize: 15, fontWeight: 700, ...row, justifyContent: 'center', gap: 8,
    };
    case 'bv2shell-up-next': return {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', padding: '10px 14px', borderRadius: 14,
      border: `1px solid ${role('line', T.separator)}`, background: role('bg', T.surfaceAlt), cursor: 'pointer', textAlign: 'left',
    };
    case 'bv2shell-answer': return {
      textAlign: 'left', padding: '11px 14px', borderRadius: 12, cursor: role('cursor', 'pointer') as CSSProperties['cursor'],
      fontSize: 14.5, lineHeight: 1.35, display: 'flex', alignItems: 'center', gap: 10,
      border: `1px solid ${role('bc', T.separator)}`, background: role('bg', T.surfaceAlt), color: role('ink', T.label),
      transition: 'background 200ms ease, border-color 200ms ease',
    };
    case 'bv2shell-next': return {
      marginTop: 10, height: 36, padding: '0 14px', borderRadius: 10, border: 'none', background: role('bg', T.accent), color: role('ink', T.label),
      fontWeight: 700, fontSize: 14, cursor: 'pointer', ...row, gap: 6,
    };
    case 'bv2shell-hero-action': return {
      display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, padding: '14px 16px', borderRadius: 16, textAlign: 'left', cursor: 'pointer',
      border: role('border', `1px solid ${T.separator}`), background: role('bg', T.surfaceAlt), color: role('ink', T.label),
    };
    case 'bv2shell-refresh': return {
      height: 26, padding: '0 12px', borderRadius: 999, border: 'none', background: role('bg', T.accent), color: role('ink', T.label),
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
    };
  }
}
